export const UIKIT_COLORS = [
	'black',
	'white',
	'primary-50',
	'primary-100',
	'primary-200',
	'primary-300',
	'primary-400',
	'primary-500',
	'primary-600',
	'primary-700',
	'primary-800',
	'primary-900',
	'primary-950',
	'primary',
	'on-primary',
	'primary-container',
	'on-primary-container',
	'on-surface',
	'on-surface-variant',
	'secondary-text',
	'placeholder-text',
	'outline-lighter',
	'outline',
	'outline-darker',
	'background-lowest',
	'background',
	'surface',
	'surface-variant-1',
	'surface-variant-2',
	'warning',
	'on-warning',
	'warning-container',
	'on-warning-container',
	'success',
	'on-success',
	'success-container',
	'on-success-container',
	'error',
	'on-error',
	'error-container',
	'on-error-container',
	'inform',
	'on-inform',
	'inform-container',
	'on-inform-container'
]

export const isUiKitColor = (color?: string | null) => {
	return !!color && UIKIT_COLORS.includes(color)
}

export const isCssColor = (color?: string | null) => {
	return !!color && /^(#|var\(--|(rgb|hsl)a?\()/.test(color)
}

// Types

export interface RGB {
	r: number
	g: number
	b: number
	a?: number
}

export type Color = string | number

// Parse a color input into an RGB object.
// Supports number (e.g. 0xff0000) and hex string (e.g. "#ff0000" or "ff0000").
export function parseColor(color: Color): RGB {
	if (typeof color === 'number') {
		// Assume number is a 24-bit hex integer (ignoring alpha)
		return {
			r: (color >> 16) & 0xff,
			g: (color >> 8) & 0xff,
			b: color & 0xff
		}
	} else if (typeof color === 'string') {
		// Remove leading '#' if present.
		let hex = color.startsWith('#') ? color.slice(1) : color
		// Expand shorthand hex (e.g. "f00" => "ff0000")
		if (hex.length === 3) {
			hex = hex
				.split('')
				.map((ch) => ch + ch)
				.join('')
		}
		if (hex.length !== 6) {
			console.warn(
				`'${color}' is not a valid hex color. Expected 3 or 6 hex characters.`
			)
		}
		const intVal = parseInt(hex, 16)
		if (isNaN(intVal)) {
			throw new TypeError(`Invalid hex color: ${color}`)
		}
		return {
			r: (intVal >> 16) & 0xff,
			g: (intVal >> 8) & 0xff,
			b: intVal & 0xff
		}
	}
	throw new TypeError(`Unsupported color format: ${color}`)
}

// Compute the relative luminance of an RGB color.
// Uses the standard formula as defined by WCAG.
export function getLuminance(rgb: RGB): number {
	const transform = (channel: number) => {
		const c = channel / 255
		return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
	}
	const R = transform(rgb.r)
	const G = transform(rgb.g)
	const B = transform(rgb.b)
	return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

// A simplified contrast function (as a stand-in for APCAcontrast).
// Returns the contrast ratio (typically between 1 and 21).
export function APCAcontrast(foreground: RGB, background: RGB): number {
	const L1 = getLuminance(foreground)
	const L2 = getLuminance(background)
	// Ensure L1 is the lighter luminance.
	const lighter = Math.max(L1, L2)
	const darker = Math.min(L1, L2)
	return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Given a background color, returns either '#fff' (white) or '#000' (black)
 * as the best foreground color based on contrast.
 *
 * The function computes the contrast ratios for white and black against the
 * given background and returns the one with higher (absolute) contrast.
 */
export function getForeground(color: Color): string {
	const bgRGB = parseColor(color)
	const blackRGB = parseColor(0) // Black: 0x000000
	const whiteRGB = parseColor(0xffffff) // White: 0xffffff

	const blackContrast = Math.abs(APCAcontrast(blackRGB, bgRGB))
	const whiteContrast = Math.abs(APCAcontrast(whiteRGB, bgRGB))

	// Here we cap the black contrast at 50 to force white if both are acceptable.
	return whiteContrast > Math.min(blackContrast, 50)
		? getComputedStyle(document.documentElement)
				.getPropertyValue(`--color-white`)
				.trim()
		: getComputedStyle(document.documentElement)
				.getPropertyValue(`--color-black`)
				.trim()
}

export function hexToHSLVars(hex: string) {
	let r = 0,
		g = 0,
		b = 0
	if (hex.length === 4) {
		r = parseInt(hex[1] + hex[1], 16)
		g = parseInt(hex[2] + hex[2], 16)
		b = parseInt(hex[3] + hex[3], 16)
	} else if (hex.length === 7) {
		r = parseInt(hex[1] + hex[2], 16)
		g = parseInt(hex[3] + hex[4], 16)
		b = parseInt(hex[5] + hex[6], 16)
	}

	r /= 255
	g /= 255
	b /= 255

	const max = Math.max(r, g, b),
		min = Math.min(r, g, b)
	let h = 0,
		s = 0

	const l = (max + min) / 2

	if (max !== min) {
		const d = max - min
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0)
				break
			case g:
				h = (b - r) / d + 2
				break
			case b:
				h = (r - g) / d + 4
				break
		}
		h /= 6
	}

	return {
		h: Math.round(h * 360),
		s: Math.round(s * 100),
		l: Math.round(l * 100)
	}
}
